import { useState, useRef, useEffect } from "react";
import { ChevronDown, SplitSquareHorizontal, Type, ImagePlus, Loader2, Smile } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const EMOJI_CATEGORIES = [
  { name: "Caras", emojis: ["😀","😃","😄","😁","😆","😅","🤣","😂","🙂","😊","😇","🥰","😍","🤩","😘","😗","😚","😋","😛","😜","🤪","😝","🤑","🤗","🤭","🤫","🤔","😐","😑","😶","😏","😒","🙄","😬","😮‍💨","🤥","😌","😔","😪","🤤","😴","😷","🤒","🤕","🤢","🤮","🥵","🥶","🥴","😵","🤯","🤠","🥳","🥸","😎","🤓","🧐"] },
  { name: "Gestos", emojis: ["👋","🤚","🖐️","✋","🖖","👌","🤌","🤏","✌️","🤞","🤟","🤘","🤙","👈","👉","👆","🖕","👇","☝️","👍","👎","✊","👊","🤛","🤜","👏","🙌","👐","🤲","🤝","🙏","💪","🦾"] },
  { name: "Corazones", emojis: ["❤️","🧡","💛","💚","💙","💜","🖤","🤍","🤎","💔","❤️‍🔥","❤️‍🩹","💕","💞","💓","💗","💖","💘","💝","💟"] },
  { name: "Objetos", emojis: ["🎉","🎊","🎈","🎁","🏆","🥇","🏅","⭐","🌟","✨","💫","🔥","💥","💯","✅","❌","⚠️","📌","📍","💰","💵","💳","📱","💻","📧","📞","🔔","🔑","🔒","⏰","📅","📊","📈","📉","🎯","💡","🚀","⚡","🛒","🏷️","📦"] },
  { name: "Flechas", emojis: ["➡️","⬅️","⬆️","⬇️","↗️","↘️","↙️","↖️","↕️","↔️","🔄","🔃","▶️","◀️","🔼","🔽","⏩","⏪","⏫","⏬","👉","👈"] },
  { name: "Simbolos", emojis: ["💲","©️","®️","™️","#️⃣","*️⃣","0️⃣","1️⃣","2️⃣","3️⃣","4️⃣","5️⃣","6️⃣","7️⃣","8️⃣","9️⃣","🔟","🆓","🆕","🆗","🔴","🟠","🟡","🟢","🔵","🟣","⚫","⚪","🟤","🔶","🔷","💠"] },
];

const VARIABLES = [
  { label: "Nombre", value: "{{nombre}}" },
  { label: "DNI", value: "{{dni}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Link 1", value: "{{link1}}" },
  { label: "Link 2", value: "{{link2}}" },
  { label: "Dato 1", value: "{{dato1}}" },
  { label: "Dato 2", value: "{{dato2}}" },
  { label: "Dato 3", value: "{{dato3}}" },
  { label: "Dato 4", value: "{{dato4}}" },
  { label: "Dato 5", value: "{{dato5}}" },
  { label: "Dato 6", value: "{{dato6}}" },
  { label: "Dato 7", value: "{{dato7}}" },
  { label: "Dato 8", value: "{{dato8}}" },
  { label: "Dato 9", value: "{{dato9}}" },
  { label: "Dato 10", value: "{{dato10}}" },
];

const SMS_LIMIT = 160;

// Calculate approximate character count (replaces variables with estimated lengths)
function estimateCharCount(text) {
  if (!text) return 0;
  // Remove spintax and pick first option for estimation
  let est = text.replace(/\{([^{}|]+)\|[^{}]+\}/g, '$1');
  // Replace variables with estimated placeholder (avg name ~10 chars)
  est = est.replace(/\{\{(\w+?)(?:L(\d+))?\}\}/gi, (_, name, limit) => {
    const maxLen = limit ? parseInt(limit) : 15;
    return 'x'.repeat(Math.min(maxLen, 15));
  });
  // Remove dividers
  est = est.replace(/\n---\n/g, '');
  return est.length;
}

export function MessageToolbar({ textareaRef, value, onChange, showCharCount = true, hideDivider = false }) {
  const [showVarMenu, setShowVarMenu] = useState(false);
  const [showLimitInput, setShowLimitInput] = useState(null);
  const [charLimit, setCharLimit] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [emojiCategory, setEmojiCategory] = useState(0);
  const menuRef = useRef(null);
  const emojiRef = useRef(null);
  const imageInputRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowVarMenu(false);
        setShowLimitInput(null);
      }
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const insertAtCursor = (text) => {
    const ta = textareaRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const newVal = value.substring(0, start) + text + value.substring(end);
    onChange(newVal);
    setTimeout(() => {
      ta.focus();
      ta.selectionStart = ta.selectionEnd = start + text.length;
    }, 0);
  };

  const insertVariable = (variable) => {
    if (showLimitInput === variable.value) {
      // Insert with limit
      const limit = parseInt(charLimit);
      const varName = variable.value.replace(/[{}]/g, '');
      const withLimit = limit > 0 ? `{{${varName}L${limit}}}` : variable.value;
      insertAtCursor(withLimit);
      setShowLimitInput(null);
      setCharLimit("");
      setShowVarMenu(false);
    } else {
      setShowLimitInput(variable.value);
      setCharLimit("");
    }
  };

  const insertVariableNoLimit = (variable) => {
    insertAtCursor(variable.value);
    setShowLimitInput(null);
    setCharLimit("");
    setShowVarMenu(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Solo se permiten imagenes");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("La imagen no puede superar 10MB");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/images/upload`, fd);
      const fullUrl = `${window.location.origin}${res.data.public_url}`;
      insertAtCursor(fullUrl);
      toast.success("Imagen subida e insertada");
    } catch (err) {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const charCount = estimateCharCount(value);
  const isOverSms = charCount > SMS_LIMIT;
  const isNearSms = charCount > SMS_LIMIT - 20;

  return (
    <div>
      <div className="flex flex-wrap items-center gap-1.5 bg-zinc-950/50 border border-zinc-800 rounded-t-md px-2 py-1.5">
        <div className="relative" ref={menuRef}>
          <button
            data-testid="insert-variable-btn"
            onClick={() => { setShowVarMenu(!showVarMenu); setShowLimitInput(null); }}
            className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-cyan-400 border border-zinc-700 rounded px-2 py-1 text-[11px] font-mono transition-colors duration-200"
            type="button"
          >
            {"{{x}}"} Variables <ChevronDown className="w-3 h-3" />
          </button>
          {showVarMenu && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-900 border border-zinc-700 rounded-md shadow-xl max-h-56 overflow-y-auto w-56">
              <div className="px-3 py-1.5 text-[9px] text-zinc-500 border-b border-zinc-800 font-mono">
                Click = sin limite | Click derecho = con limite
              </div>
              {VARIABLES.map(v => (
                <div key={v.value}>
                  <button
                    onClick={() => insertVariableNoLimit(v)}
                    onContextMenu={(e) => { e.preventDefault(); insertVariable(v); }}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-800 text-zinc-300 hover:text-cyan-400 transition-colors duration-200 flex justify-between items-center"
                  >
                    <span>{v.label}</span>
                    <span className="text-[10px] font-mono text-zinc-600">{v.value}</span>
                  </button>
                  {showLimitInput === v.value && (
                    <div className="flex items-center gap-1 px-3 py-1.5 bg-zinc-800/50 border-t border-zinc-700/50">
                      <Type className="w-3 h-3 text-amber-400" />
                      <input
                        type="number"
                        min="1"
                        max="999"
                        value={charLimit}
                        onChange={(e) => setCharLimit(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') insertVariable(v); }}
                        placeholder="Max chars"
                        className="w-20 bg-zinc-900 border border-zinc-600 rounded px-1.5 py-0.5 text-[11px] text-amber-400 font-mono outline-none focus:border-amber-400"
                        autoFocus
                      />
                      <button
                        onClick={() => insertVariable(v)}
                        className="bg-amber-600 hover:bg-amber-500 text-black text-[10px] font-bold px-2 py-0.5 rounded"
                        type="button"
                      >
                        OK
                      </button>
                      <button
                        onClick={() => { insertVariableNoLimit(v); }}
                        className="text-[10px] text-zinc-500 hover:text-zinc-300 px-1"
                        type="button"
                      >
                        Sin limite
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        {!hideDivider && (
          <button
            data-testid="insert-divider-btn"
            onClick={() => insertAtCursor('\n---\n')}
            className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-amber-400 border border-zinc-700 rounded px-2 py-1 text-[11px] font-mono transition-colors duration-200"
            type="button"
          >
            <SplitSquareHorizontal className="w-3 h-3" /> Divisor ---
          </button>
        )}
        <button
          onClick={() => insertAtCursor('{opcion1|opcion2}')}
          className="flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-emerald-400 border border-zinc-700 rounded px-2 py-1 text-[11px] font-mono transition-colors duration-200"
          type="button"
        >
          {"{A|B}"} Spintax
        </button>
        <label
          data-testid="insert-image-btn"
          className={`flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-blue-400 border border-zinc-700 rounded px-2 py-1 text-[11px] font-mono transition-colors duration-200 cursor-pointer ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
          {uploading ? "Subiendo..." : "Imagen"}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
            disabled={uploading}
          />
        </label>

        {/* Emoji Picker */}
        <div className="relative" ref={emojiRef}>
          <button
            data-testid="insert-emoji-btn"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className={`flex items-center gap-1 bg-zinc-800 hover:bg-zinc-700 text-orange-400 border border-zinc-700 rounded px-2 py-1 text-[11px] font-mono transition-colors duration-200 ${showEmojiPicker ? 'bg-zinc-700 border-orange-500/50' : ''}`}
            type="button"
          >
            <Smile className="w-3 h-3" /> Emoji
          </button>
          {showEmojiPicker && (
            <div className="absolute top-full left-0 mt-1 z-50 bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl w-72">
              <div className="flex gap-0.5 px-2 pt-2 pb-1 border-b border-zinc-800 overflow-x-auto">
                {EMOJI_CATEGORIES.map((cat, i) => (
                  <button
                    key={cat.name}
                    onClick={() => setEmojiCategory(i)}
                    className={`px-2 py-1 rounded text-[10px] font-mono whitespace-nowrap transition-colors ${
                      emojiCategory === i
                        ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
                        : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800'
                    }`}
                    type="button"
                  >
                    {cat.emojis[0]} {cat.name}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-8 gap-0.5 p-2 max-h-48 overflow-y-auto">
                {EMOJI_CATEGORIES[emojiCategory].emojis.map((emoji, i) => (
                  <button
                    key={`${emoji}-${i}`}
                    onClick={() => insertAtCursor(emoji)}
                    className="w-8 h-8 flex items-center justify-center text-lg hover:bg-zinc-800 rounded transition-colors"
                    type="button"
                    title={emoji}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {showCharCount && (
          <div className="ml-auto flex items-center gap-1.5">
            <span data-testid="char-counter" className={`text-[11px] font-mono ${
              isOverSms ? 'text-red-400' : isNearSms ? 'text-amber-400' : 'text-zinc-500'
            }`}>
              ~{charCount}/{SMS_LIMIT}
            </span>
            {isOverSms && (
              <span data-testid="sms-warning" className="text-[9px] bg-red-500/20 text-red-400 border border-red-500/30 rounded px-1.5 py-0.5 font-mono">
                Excede SMS
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
